import { REGARD, projeter } from '../figures/solides.js';
import { LARGEUR_TOILE } from '../types/fiche-cours.js';
/* ───────────────────────────────── le format ───────────────────────────────── */
export const FORMES_DE_SOLIDE = [
    'cube',
    'pave',
    'prisme_triangulaire',
    'pyramide',
    'cylindre',
    'cone',
    'boule',
    'demi_boule',
    'compose',
];
export const POSES_DE_DEMI_BOULE = ['dome', 'bol'];
export const VUES_DE_SOLIDE = ['perspective', 'developpement', 'les_deux'];
export const GRANDEURS_DE_SOLIDE = [
    'arete',
    'longueur',
    'largeur',
    'hauteur',
    'cote',
    'cote_a',
    'cote_b',
    'cote_c',
    /* La hauteur du triangle de BASE d'un prisme, relative à son premier côté (`cote_a`). */
    'hauteur_base',
    'apotheme',
    'rayon',
    'diametre',
    'circonference',
];
/* ───────────────────────────────── les mesures ───────────────────────────────── */
const fr = (n) => String(Math.round(n * 1000) / 1000)
    .replace('.', ',')
    .replace('-', '−');
/** L'apothème d'une pyramide droite à base carrée (la hauteur d'une face) ou d'un cône (sa génératrice). */
export function apothemeDe(s) {
    if (s.forme === 'cone')
        return Math.hypot(s.hauteur, s.rayon);
    return s.apotheme ?? Math.hypot(s.hauteur ?? 0, s.cote / 2);
}
/**
 * Le sommet de l'angle droit d'une base de prisme : 0 (entre `cote_a` et
 * `cote_c`), 1 (entre `cote_a` et `cote_b`), 2 (entre `cote_b` et `cote_c`) —
 * ou `null` si le triangle n'est pas rectangle.
 */
export function angleDroitDeLaBase(cotes) {
    const [a, b, c] = cotes;
    const droit = (x, y, hyp) => Math.abs(x * x + y * y - hyp * hyp) <= 1e-9 * Math.max(1, hyp * hyp);
    return droit(a, c, b) ? 0 : droit(a, b, c) ? 1 : droit(b, c, a) ? 2 : null;
}
/**
 * La hauteur du triangle de base relative à son premier côté : sa longueur, et
 * où tombe son pied le long de ce côté (de 0, au sommet 0, à `cote_a`, au
 * sommet 1). Un pied hors de ce côté : la hauteur tombe dehors (angle obtus).
 */
export function hauteurDeLaBase(cotes) {
    const [a, b, c] = cotes;
    const pied = (a * a + c * c - b * b) / (2 * a);
    return { hauteur: Math.sqrt(Math.max(0, c * c - pied * pied)), pied };
}
/** La hauteur d'une pyramide : écrite, ou déduite de son côté et de son apothème (0 si l'apothème est trop court). */
export function hauteurDePyramide(s) {
    if (s.hauteur !== undefined)
        return s.hauteur;
    const ap = s.apotheme ?? 0;
    return Math.sqrt(Math.max(0, ap * ap - (s.cote / 2) ** 2));
}
/** La valeur d'une grandeur d'un solide, ou `null` quand ce solide ne l'a pas. */
export function valeurDeGrandeur(s, g) {
    switch (s.forme) {
        case 'cube':
            return g === 'arete' ? s.arete : null;
        case 'pave':
            return g === 'longueur' || g === 'largeur' || g === 'hauteur' ? s[g] : null;
        case 'prisme_triangulaire':
            if (g === 'hauteur')
                return s.hauteur;
            if (g === 'hauteur_base')
                return hauteurDeLaBase(s.cotes).hauteur;
            return g === 'cote_a'
                ? s.cotes[0]
                : g === 'cote_b'
                    ? s.cotes[1]
                    : g === 'cote_c'
                        ? s.cotes[2]
                        : null;
        case 'pyramide':
            return g === 'cote'
                ? s.cote
                : g === 'hauteur'
                    ? hauteurDePyramide(s)
                    : g === 'apotheme'
                        ? apothemeDe(s)
                        : null;
        case 'cylindre':
        case 'cone':
            if (g === 'rayon')
                return s.rayon;
            if (g === 'diametre')
                return 2 * s.rayon;
            if (g === 'hauteur')
                return s.hauteur;
            /* Le tour de la base : la longueur du rectangle d'un cylindre déroulé, l'ARC du secteur d'un cône. */
            if (g === 'circonference')
                return 2 * Math.PI * s.rayon;
            return g === 'apotheme' && s.forme === 'cone' ? apothemeDe(s) : null;
        case 'boule':
        case 'demi_boule':
            return g === 'rayon' ? s.rayon : g === 'diametre' ? 2 * s.rayon : null;
    }
}
/** Les grandeurs qu'une VUE sait montrer d'un solide. La hauteur d'une pyramide ne se lit pas sur son développement. */
function grandeursMontrees(s, vue) {
    const p = vue === 'perspective';
    switch (s.forme) {
        case 'cube':
            return ['arete'];
        case 'pave':
            return ['longueur', 'largeur', 'hauteur'];
        case 'prisme_triangulaire':
            return ['cote_a', 'cote_b', 'cote_c', 'hauteur', 'hauteur_base'];
        case 'pyramide':
            return p ? ['cote', 'hauteur', 'apotheme'] : ['cote', 'apotheme'];
        case 'cylindre':
            return p
                ? ['rayon', 'diametre', 'hauteur']
                : ['rayon', 'diametre', 'hauteur', 'circonference'];
        case 'cone':
            return p
                ? ['rayon', 'diametre', 'hauteur', 'apotheme']
                : ['rayon', 'diametre', 'apotheme', 'circonference'];
        case 'boule':
        case 'demi_boule':
            return p ? ['rayon', 'diametre'] : [];
    }
}
/* ───────────────────────────── le patron d'un cube ───────────────────────────── */
/** La croix classique : quatre carrés en bande, un au-dessus et un en dessous du deuxième. */
export const PATRON_EN_CROIX = [
    [0, 0],
    [1, 0],
    [2, 0],
    [3, 0],
    [1, 1],
    [1, -1],
];
const cleDeCase = (c) => `${c[0]}:${c[1]}`;
/** Les cases sont-elles d'un seul tenant (voisines par un côté) ? */
export function patronConnexe(cases) {
    if (cases.length === 0)
        return false;
    const toutes = new Set(cases.map(cleDeCase));
    const vues = new Set();
    const file = [cases[0]];
    while (file.length > 0) {
        const c = file.pop();
        if (vues.has(cleDeCase(c)))
            continue;
        vues.add(cleDeCase(c));
        for (const v of [
            [c[0] + 1, c[1]],
            [c[0] - 1, c[1]],
            [c[0], c[1] + 1],
            [c[0], c[1] - 1],
        ]) {
            if (toutes.has(cleDeCase(v)) && !vues.has(cleDeCase(v)))
                file.push(v);
        }
    }
    return vues.size === toutes.size;
}
/**
 * CE PATRON SE REFERME-T-IL EN CUBE ? On fait ROULER un cube sur les cases :
 * à chaque pas, la face qui touche la table change. Le patron est celui d'un
 * cube quand il a six cases d'un seul tenant et que les six faces du cube ont
 * touché la table, chacune une fois. Ni modèle, ni tâtonnement.
 */
export function patronSePlie(cases) {
    if (cases.length !== 6 || new Set(cases.map(cleDeCase)).size !== 6 || !patronConnexe(cases)) {
        return false;
    }
    const rouler = {
        est: ([b, h, n, s, e, o]) => [e, o, n, s, h, b],
        ouest: ([b, h, n, s, e, o]) => [o, e, n, s, b, h],
        nord: ([b, h, n, s, e, o]) => [n, s, h, b, e, o],
        sud: ([b, h, n, s, e, o]) => [s, n, b, h, e, o],
    };
    const toutes = new Set(cases.map(cleDeCase));
    const poses = new Map();
    const file = [
        { c: cases[0], p: [0, 1, 2, 3, 4, 5] },
    ];
    while (file.length > 0) {
        const { c, p } = file.pop();
        if (poses.has(cleDeCase(c)))
            continue;
        poses.set(cleDeCase(c), p);
        const voisins = [
            [[c[0] + 1, c[1]], 'est'],
            [[c[0] - 1, c[1]], 'ouest'],
            [[c[0], c[1] + 1], 'nord'],
            [[c[0], c[1] - 1], 'sud'],
        ];
        for (const [v, sens] of voisins) {
            if (toutes.has(cleDeCase(v)) && !poses.has(cleDeCase(v))) {
                file.push({ c: v, p: rouler[sens](p) });
            }
        }
    }
    return new Set([...poses.values()].map((p) => p[0])).size === 6;
}
const SURFACES_FAMILLE = [
    'teinte',
    'ardoise',
    'jeton_efface',
    'teinte_forte',
];
const LETTRES = ['A', 'B', 'C', 'D'];
/** Les rectangles latéraux d'un prisme triangulaire, regroupés par largeur : le rang de famille de chacun. */
function famillesDuPrisme(cotes) {
    const largeurs = [];
    const rangs = cotes.map((c) => {
        const i = largeurs.findIndex((l) => Math.abs(l - c) < 1e-9);
        if (i >= 0)
            return i;
        largeurs.push(c);
        return largeurs.length - 1;
    });
    return { rangs, largeurs };
}
/** Les familles de faces d'un solide, dans l'ordre de leurs lettres. Vide : une boule, un solide décomposable. */
export function famillesDeFaces(s, unite = '') {
    const u = (v) => (unite === '' ? fr(v) : `${fr(v)} ${unite}`);
    const liste = (() => {
        switch (s.forme) {
            case 'cube': {
                const n = s.patron?.length ?? 6;
                return [{ nombre: n, quoi: `${n} carrés de côté ${u(s.arete)}` }];
            }
            case 'pave':
                return [
                    { nombre: 2, quoi: `2 rectangles de ${u(s.longueur)} sur ${u(s.hauteur)}` },
                    { nombre: 2, quoi: `2 rectangles de ${u(s.largeur)} sur ${u(s.hauteur)}` },
                    { nombre: 2, quoi: `2 rectangles de ${u(s.longueur)} sur ${u(s.largeur)}` },
                ];
            case 'prisme_triangulaire': {
                const { rangs, largeurs } = famillesDuPrisme(s.cotes);
                return [
                    { nombre: 2, quoi: '2 triangles : les bases' },
                    ...largeurs.map((l, i) => {
                        const n = rangs.filter((r) => r === i).length;
                        return {
                            nombre: n,
                            quoi: `${n} rectangle${n > 1 ? 's' : ''} de ${u(l)} sur ${u(s.hauteur)}`,
                        };
                    }),
                ];
            }
            case 'pyramide':
                return [
                    { nombre: 1, quoi: `1 carré de côté ${u(s.cote)} : la base` },
                    { nombre: 4, quoi: '4 triangles isocèles : les faces latérales' },
                ];
            case 'cylindre':
                return [
                    { nombre: 2, quoi: `2 disques de rayon ${u(s.rayon)} : les bases` },
                    { nombre: 1, quoi: '1 rectangle : la face latérale, déroulée' },
                ];
            case 'cone':
                return [
                    { nombre: 1, quoi: `1 disque de rayon ${u(s.rayon)} : la base` },
                    { nombre: 1, quoi: '1 secteur de disque : la face latérale, déroulée' },
                ];
            default:
                return [];
        }
    })();
    return liste.map((f, i) => ({
        ...f,
        etiquette: LETTRES[i],
        surface: SURFACES_FAMILLE[i],
    }));
}
/* ───────────────────────────────── le contrôle ───────────────────────────────── */
const TEXTE_DE_COTE_MAX = 18;
function griefsDeSimple(s, ou) {
    const g = [];
    const positif = (nom, v) => {
        if (!(Number.isFinite(v) && v > 0))
            g.push(`${ou}${nom} : une longueur est un nombre positif`);
    };
    switch (s.forme) {
        case 'cube':
            positif('arete', s.arete);
            break;
        case 'pave':
            positif('longueur', s.longueur);
            positif('largeur', s.largeur);
            positif('hauteur', s.hauteur);
            break;
        case 'prisme_triangulaire': {
            s.cotes.forEach((c, i) => positif(`cotes[${i}]`, c));
            positif('hauteur', s.hauteur);
            const [a, b, c] = s.cotes;
            if (a > 0 && b > 0 && c > 0 && !(a < b + c && b < a + c && c < a + b)) {
                g.push(`${ou}cotes : ${fr(a)}, ${fr(b)} et ${fr(c)} ne ferment pas un triangle`);
            }
            else if (s.angleDroit === true && angleDroitDeLaBase(s.cotes) === null) {
                g.push(`${ou}angleDroit : la base de côtés ${fr(a)}, ${fr(b)} et ${fr(c)} n’est pas un triangle rectangle`);
            }
            break;
        }
        case 'pyramide':
            positif('cote', s.cote);
            if ((s.hauteur === undefined) === (s.apotheme === undefined)) {
                g.push(`${ou} : une pyramide se donne par sa hauteur OU par son apothème — l’un, pas les deux`);
            }
            else if (s.hauteur !== undefined) {
                positif('hauteur', s.hauteur);
            }
            else if (s.apotheme !== undefined) {
                positif('apotheme', s.apotheme);
                /* L'apothème est l'hypoténuse du triangle (hauteur, demi-côté) : il dépasse le demi-côté. */
                if (Number.isFinite(s.apotheme) && Number.isFinite(s.cote) && !(s.apotheme > s.cote / 2)) {
                    g.push(`${ou} : l’apothème (${fr(s.apotheme)}) doit dépasser la moitié du côté (${fr(s.cote / 2)}) — sinon la pyramide est plate`);
                }
            }
            break;
        case 'cylindre':
        case 'cone':
            positif('rayon', s.rayon);
            positif('hauteur', s.hauteur);
            break;
        case 'boule':
        case 'demi_boule':
            positif('rayon', s.rayon);
            break;
    }
    return g;
}
/** L'emprise au sol d'un solide posé : sa largeur (frontale) et sa profondeur. */
function empriseDe(s) {
    switch (s.forme) {
        case 'cube':
            return { largeur: s.arete, profondeur: s.arete, hauteur: s.arete };
        case 'pave':
            return { largeur: s.longueur, profondeur: s.largeur, hauteur: s.hauteur };
        case 'prisme_triangulaire':
            return { largeur: s.cotes[0], profondeur: s.hauteur, hauteur: 0 };
        case 'pyramide':
            return { largeur: s.cote, profondeur: s.cote, hauteur: hauteurDePyramide(s) };
        case 'cylindre':
        case 'cone':
            return { largeur: 2 * s.rayon, profondeur: 2 * s.rayon, hauteur: s.hauteur };
        case 'boule':
            return { largeur: 2 * s.rayon, profondeur: 2 * s.rayon, hauteur: 2 * s.rayon };
        case 'demi_boule':
            return { largeur: 2 * s.rayon, profondeur: 2 * s.rayon, hauteur: s.rayon };
    }
}
const estRond = (x) => x.forme === 'cylindre' || x.forme === 'cone' || x.forme === 'boule' || x.forme === 'demi_boule';
/**
 * CE QUI TIENT SUR QUOI (tranche T37). Vide : l'assemblage se dessine.
 *  - sur un cylindre : un corps rond (cylindre, cône, boule, demi-boule) ;
 *  - sur un cube ou un pavé : un polyèdre, ou un cône (dessiné dans la même
 *    perspective cavalière que le pavé) ;
 *  - sur un cône RENVERSÉ (pointe en bas) : un cône, une demi-boule, un cylindre.
 */
function griefsDAssemblage(s) {
    const { bas, haut } = s;
    const g = [];
    if (haut.forme === 'cone' && haut.renverse === true) {
        g.push('haut.renverse : un cône renversé est le BAS d’un solide décomposable, pas le haut');
    }
    if (haut.forme === 'demi_boule' && haut.pose === 'bol') {
        g.push('haut.pose : sur un autre solide, une demi-boule est posée sur sa face plane (« dome »)');
    }
    if (bas.forme === 'cone') {
        if (bas.renverse !== true) {
            g.push('bas : rien ne tient sur la pointe d’un cône — pour un cône pointe en bas (cornet, toupie), écris « renverse »: true');
        }
        else if (!(haut.forme === 'cone' ||
            haut.forme === 'demi_boule' ||
            haut.forme === 'cylindre')) {
            g.push('sur un cône renversé se pose un cône, une demi-boule ou un cylindre');
        }
    }
    else if (estRond(bas) !== estRond(haut) && !(haut.forme === 'cone' && !estRond(bas))) {
        g.push('un solide décomposable assemble deux corps ronds (cylindre, cône, boule, demi-boule) ou deux polyèdres (cube, pavé, pyramide) — seul le cône se pose aussi sur un cube ou un pavé');
    }
    return g;
}
/** Ce qu'un schéma ne voit pas d'un solide illustré. Vide : il se dessine en entier. */
export function griefsDeSolide(d, options = {}) {
    const g = [];
    const s = d.solide;
    const vues = d.vue === 'les_deux' ? ['perspective', 'developpement'] : [d.vue];
    if (s.forme === 'compose') {
        g.push(...griefsDeSimple(s.bas, 'bas.'), ...griefsDeSimple(s.haut, 'haut.'));
        if (g.length === 0) {
            const b = empriseDe(s.bas);
            const h = empriseDe(s.haut);
            if (h.largeur > b.largeur + 1e-9 || h.profondeur > b.profondeur + 1e-9) {
                g.push('le solide du haut dépasse celui du bas : il ne tiendrait pas dessus');
            }
            g.push(...griefsDAssemblage(s));
        }
        if (d.vue !== 'perspective') {
            g.push('un solide décomposable se montre en perspective : son développement n’est pas dessiné');
        }
        if (d.faces === true)
            g.push('les faces d’un solide décomposable ne sont pas repérées');
    }
    else {
        g.push(...griefsDeSimple(s, ''));
        if (s.forme === 'boule' || s.forme === 'demi_boule') {
            const nom = s.forme === 'boule' ? 'boule' : 'demi-boule';
            if (d.vue !== 'perspective') {
                g.push(`la surface d’une ${nom} ne se met pas à plat : elle n’a pas de développement`);
            }
            if (d.faces === true)
                g.push(`une ${nom} n’a pas de faces à repérer`);
        }
        if (s.forme === 'cone' && s.renverse === true) {
            g.push('renverse : un cône renversé est le BAS d’un solide décomposable (cornet, toupie)');
        }
        if (s.forme === 'cube' && s.patron !== undefined) {
            const cases = s.patron;
            if (cases.some((c) => !Number.isInteger(c[0]) || !Number.isInteger(c[1]))) {
                g.push('patron : chaque case est un couple d’entiers [colonne, rangée]');
            }
            else if (new Set(cases.map(cleDeCase)).size !== cases.length) {
                g.push('patron : une case est écrite deux fois');
            }
            else if (cases.length < 4 || cases.length > 8) {
                g.push('patron : de 4 à 8 cases');
            }
            else if (!patronConnexe(cases)) {
                g.push('patron : les cases ne sont pas d’un seul tenant');
            }
            else {
                const xs = cases.map((c) => c[0]);
                const ys = cases.map((c) => c[1]);
                if (Math.max(...xs) - Math.min(...xs) > 5 || Math.max(...ys) - Math.min(...ys) > 5) {
                    g.push('patron : il tient dans six cases de large et de haut');
                }
                else if (options.patronFautifPermis !== true && !patronSePlie(cases)) {
                    g.push(cases.length === 6
                        ? 'patron : ces six carrés ne se referment pas en cube (deux faces se recouvrent)'
                        : `patron : un cube a six faces, ce patron en a ${cases.length}`);
                }
            }
            if (d.vue === 'perspective')
                g.push('patron : il ne se voit que dans un développement');
        }
    }
    if (d.unite !== undefined && (d.unite.trim() === '' || [...d.unite].length > 6)) {
        g.push('unite : de 1 à 6 caractères (« cm », « m »)');
    }
    const dites = new Set();
    (d.cotes ?? []).forEach((c, i) => {
        const ou = `cotes[${i}]`;
        const cle = `${c.partie ?? ''}:${c.grandeur}`;
        if (dites.has(cle))
            g.push(`${ou} : « ${c.grandeur} » est cotée deux fois`);
        dites.add(cle);
        if (c.texte !== undefined &&
            (c.texte.trim() === '' || [...c.texte].length > TEXTE_DE_COTE_MAX)) {
            g.push(`${ou} : le texte d’une cote a de 1 à ${TEXTE_DE_COTE_MAX} caractères`);
        }
        let partie;
        if (s.forme === 'compose') {
            if (c.partie === undefined) {
                g.push(`${ou} : dans un solide décomposable, une cote dit sa partie (« bas » ou « haut »)`);
                return;
            }
            partie = s[c.partie];
        }
        else {
            if (c.partie !== undefined)
                g.push(`${ou} : « partie » ne vaut que pour un solide décomposable`);
            partie = s;
        }
        const valeur = valeurDeGrandeur(partie, c.grandeur);
        if (valeur === null) {
            g.push(`${ou} : un solide « ${partie.forme} » n’a pas de grandeur « ${c.grandeur} »`);
            return;
        }
        if (c.grandeur === 'hauteur_base' && partie.forme === 'prisme_triangulaire') {
            const { pied } = hauteurDeLaBase(partie.cotes);
            const a = partie.cotes[0];
            if (pied < 1e-9 * a || pied > a * (1 - 1e-9)) {
                g.push(pied < -1e-9 * a || pied > a * (1 + 1e-9)
                    ? `${ou} : la hauteur de la base tombe HORS de son premier côté (angle obtus) — écris le plus grand côté en premier`
                    : `${ou} : la hauteur de la base relative à son premier côté EST un de ses côtés (triangle rectangle) — cote ce côté, et marque « angleDroit »`);
            }
        }
        if (c.grandeur === 'circonference' && partie.forme === 'cone' && c.texte === undefined) {
            g.push(`${ou} : l’arc du secteur d’un cône a la longueur du tour de sa base — donne son texte (« ? », « 10π cm »)`);
        }
        if (!vues.some((v) => grandeursMontrees(partie, v).includes(c.grandeur))) {
            g.push(`${ou} : « ${c.grandeur} » ne se lit pas ${d.vue === 'developpement' ? 'sur le développement' : 'en perspective'} de ce solide`);
        }
        if (c.texte === undefined &&
            c.grandeur !== 'circonference' &&
            Math.abs(valeur * 100 - Math.round(valeur * 100)) > 1e-6) {
            g.push(`${ou} : « ${c.grandeur} » vaut ${fr(valeur)}… — elle ne s’écrit pas juste : donne son texte, ou des dimensions où elle tombe juste`);
        }
    });
    /* Sur un cône renversé, le rayon du bas et celui du haut sont portés par le MÊME trait. */
    if (s.forme === 'compose' && s.bas.forme === 'cone' && estRond(s.haut)) {
        const large = (p) => (d.cotes ?? []).some((c) => c.partie === p && (c.grandeur === 'rayon' || c.grandeur === 'diametre'));
        if (large('bas') && large('haut')) {
            g.push('cotes : le rayon (ou le diamètre) du cône renversé et celui du solide posé dessus sont sur le même trait — cote l’un des deux');
        }
    }
    return g;
}
const sceneVide = () => ({
    faces: [],
    pleins: [],
    caches: [],
    lettres: [],
    cotes: [],
    equerres: [],
});
function fusion(a, b, decalage) {
    const d = (p) => ({ x: p.x + decalage.x, y: p.y + decalage.y });
    return {
        faces: [...a.faces, ...b.faces.map((f) => ({ ...f, pts: f.pts.map(d) }))],
        pleins: [...a.pleins, ...b.pleins.map((l) => l.map(d))],
        caches: [...a.caches, ...b.caches.map((l) => l.map(d))],
        lettres: [...a.lettres, ...b.lettres.map((l) => ({ ...l, p: d(l.p) }))],
        cotes: [...a.cotes, ...b.cotes.map((c) => ({ ...c, a: d(c.a), b: d(c.b) }))],
        equerres: [...a.equerres, ...b.equerres.map((e) => ({ ...e, coin: d(e.coin) }))],
    };
}
const v3 = (x, y, z) => ({ x, y, z });
const moins = (a, b) => v3(a.x - b.x, a.y - b.y, a.z - b.z);
const croix = (a, b) => v3(a.y * b.z - a.z * b.y, a.z * b.x - a.x * b.z, a.x * b.y - a.y * b.x);
const scal = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
const milieu3 = (ps) => v3(ps.reduce((s, p) => s + p.x, 0) / ps.length, ps.reduce((s, p) => s + p.y, 0) / ps.length, ps.reduce((s, p) => s + p.z, 0) / ps.length);
/**
 * Un polyèdre CONVEXE, en perspective cavalière. Une face est visible quand
 * sa normale SORTANTE regarde l'observateur — la sortante se calcule (elle
 * s'éloigne du centre du solide), elle ne dépend pas de l'ordre d'écriture.
 * Une arête est cachée quand aucune de ses deux faces n'est visible.
 */
function scenePolyedre(p, h) {
    const sc = sceneVide();
    const centre = milieu3(p.sommets);
    const vues = p.faces.map((f) => {
        const pts = f.s.map((i) => p.sommets[i]);
        const c = milieu3(pts);
        let n = croix(moins(pts[1], pts[0]), moins(pts[2], pts[0]));
        if (scal(n, moins(c, centre)) < 0)
            n = v3(-n.x, -n.y, -n.z);
        return { f, pts, c, visible: scal(n, REGARD) > 1e-9, proche: scal(c, REGARD) };
    });
    const aretes = new Map();
    for (const v of vues) {
        v.f.s.forEach((a, i) => {
            const b = v.f.s[(i + 1) % v.f.s.length];
            const k = a < b ? `${a}|${b}` : `${b}|${a}`;
            const e = aretes.get(k);
            aretes.set(k, { a, b, visible: (e?.visible ?? false) || v.visible });
        });
    }
    for (const v of vues.filter((x) => x.visible).sort((x, y) => x.proche - y.proche)) {
        sc.faces.push({ pts: v.pts.map(projeter), surface: h.surface(v.f.famille) });
        const lettre = h.lettre(v.f.famille);
        if (lettre !== null && v.f.muette !== true)
            sc.lettres.push({ p: projeter(v.c), t: lettre });
    }
    for (const e of aretes.values()) {
        const ligne = [projeter(p.sommets[e.a]), projeter(p.sommets[e.b])];
        (e.visible ? sc.pleins : sc.caches).push(ligne);
    }
    return sc;
}
/**
 * LES CORPS RONDS ne sont pas dessinés en perspective cavalière : un cercle
 * horizontal y devient une ellipse PENCHÉE, que nul manuel ne dessine. Ils
 * sont vus de face et d'un peu haut — le cercle devient une ellipse couchée,
 * l'axe reste vertical, les mesures frontales (rayon, hauteur) se lisent.
 */
const APLATI = 0.32;
const rond = (p) => ({ x: p.x, y: p.y + APLATI * p.z });
/** L'angle du point du cercle le plus PROCHE de l'observateur (z le plus négatif). */
const PHI = (3 * Math.PI) / 2;
const N_ARC = 48;
const VUE_RONDE = { P: rond, phi: PHI };
/** La perspective cavalière des polyèdres : un cône posé sur un pavé est dessiné dans la MÊME. */
const VUE_CAVALIERE = { P: projeter, phi: Math.atan2(REGARD.z, REGARD.x) };
/** Un arc du cercle horizontal de rayon `r` à la hauteur `y`, de l'angle `t0` à `t1`, projeté. */
function arc(r, y, t0, t1, P = rond, finesse = 1) {
    const n = Math.max(4, Math.ceil((Math.abs(t1 - t0) / (2 * Math.PI)) * N_ARC * finesse));
    return Array.from({ length: n + 1 }, (_, i) => {
        const t = t0 + ((t1 - t0) * i) / n;
        return P(v3(r * Math.cos(t), y, r * Math.sin(t)));
    });
}
/**
 * Le contour d'un cône : son sommet, le centre de sa base, et les deux
 * génératrices du contour — les tangentes menées du sommet à la base PROJETÉE.
 * `devant` est l'arc de la base qui passe par le point le plus proche de
 * l'observateur, d'une tangente à l'autre ; `derriere`, le reste.
 */
function contourDeCone(r, yBase, ySommet, vue) {
    const A = vue.P(v3(0, ySommet, 0));
    const C = vue.P(v3(0, yBase, 0));
    let tMin = 0;
    let tMax = 0;
    let aMin = Infinity;
    let aMax = -Infinity;
    for (let i = 0; i < 720; i += 1) {
        const t = (i / 720) * 2 * Math.PI;
        const p = vue.P(v3(r * Math.cos(t), yBase, r * Math.sin(t)));
        const ux = C.x - A.x;
        const uy = C.y - A.y;
        const wx = p.x - A.x;
        const wy = p.y - A.y;
        const angle = Math.atan2(ux * wy - uy * wx, ux * wx + uy * wy);
        if (angle < aMin)
            [aMin, tMin] = [angle, t];
        if (angle > aMax)
            [aMax, tMax] = [angle, t];
    }
    const tour = (t, depuis) => {
        let v = t;
        while (v < depuis)
            v += 2 * Math.PI;
        while (v >= depuis + 2 * Math.PI)
            v -= 2 * Math.PI;
        return v;
    };
    const fin = tour(tMax, tMin);
    const parDevant = tour(vue.phi, tMin) <= fin;
    const a = (t0, t1) => arc(r, yBase, t0, t1, vue.P);
    return {
        A,
        C,
        devant: parDevant ? a(tMin, fin) : a(fin, tMin + 2 * Math.PI),
        derriere: parDevant ? a(fin, tMin + 2 * Math.PI) : a(tMin, fin),
    };
}
/**
 * Sépare une ligne en ce qui se VOIT et ce qu'un autre solide CACHE (le milieu
 * de chaque pas décide ; un segment droit est d'abord découpé en pas courts).
 */
function departager(l, cache) {
    const pts = l.length > 2
        ? [...l]
        : l.length === 2
            ? Array.from({ length: 49 }, (_, i) => ({
                x: l[0].x + ((l[1].x - l[0].x) * i) / 48,
                y: l[0].y + ((l[1].y - l[0].y) * i) / 48,
            }))
            : [];
    const sortie = { pleins: [], caches: [] };
    let courant = [];
    let etat = null;
    for (let i = 0; i + 1 < pts.length; i += 1) {
        const [a, b] = [pts[i], pts[i + 1]];
        const cachee = cache({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
        if (etat !== null && cachee !== etat) {
            (etat ? sortie.caches : sortie.pleins).push(courant);
            courant = [];
        }
        if (courant.length === 0)
            courant.push(a);
        courant.push(b);
        etat = cachee;
    }
    if (etat !== null)
        (etat ? sortie.caches : sortie.pleins).push(courant);
    /* Une ligne que rien ne cache reste ce qu'elle était : deux points, pas quarante-neuf. */
    if (sortie.caches.length === 0)
        return { pleins: [[...l]], caches: [] };
    return sortie;
}
/**
 * Ce que le solide posé sur un autre CACHE, dans le plan du dessin : son
 * contour au-dessus de la face de contact, à la hauteur `y0` (en unités du
 * solide, le solide du bas posé en 0).
 */
function masqueDe(haut, y0, vue) {
    const EPS = 1e-6;
    switch (haut.forme) {
        case 'cone': {
            const { A, devant } = contourDeCone(haut.rayon, y0, y0 + haut.hauteur, vue);
            const [b, c] = [devant[0], devant[devant.length - 1]];
            const signe = (p, u, w) => (w.x - u.x) * (p.y - u.y) - (w.y - u.y) * (p.x - u.x);
            const sens = Math.sign(signe(c, A, b));
            return (p) => sens * signe(p, A, b) > EPS && sens * signe(p, b, c) > EPS && sens * signe(p, c, A) > EPS;
        }
        case 'cylindre': {
            const { rayon: r, hauteur: H } = haut;
            return (p) => Math.abs(p.x) < r - EPS &&
                p.y > y0 + EPS &&
                (p.y < y0 + H || (p.x / r) ** 2 + ((p.y - y0 - H) / (APLATI * r)) ** 2 < 1 - EPS);
        }
        case 'demi_boule':
            return (p) => p.y > y0 + EPS && p.x ** 2 + (p.y - y0) ** 2 < haut.rayon ** 2 * (1 - EPS);
        case 'boule':
            return (p) => p.x ** 2 + (p.y - y0 - haut.rayon) ** 2 < haut.rayon ** 2 * (1 - EPS);
        default:
            return () => false;
    }
}
function perspectiveDe(s, h, ctx = {}) {
    const cote = (g, c, sc) => {
        const t = h.cote(g);
        if (t !== null)
            sc.cotes.push({ ...c, texte: t });
    };
    switch (s.forme) {
        case 'cube':
        case 'pave': {
            const L = s.forme === 'cube' ? s.arete : s.longueur;
            const l = s.forme === 'cube' ? s.arete : s.largeur;
            const H = s.forme === 'cube' ? s.arete : s.hauteur;
            const sommets = [0, H].flatMap((y) => [
                v3(-L / 2, y, -l / 2),
                v3(L / 2, y, -l / 2),
                v3(L / 2, y, l / 2),
                v3(-L / 2, y, l / 2),
            ]);
            const unique = s.forme === 'cube';
            const sc = scenePolyedre({
                sommets,
                faces: [
                    { s: [0, 1, 5, 4], famille: 0 },
                    { s: [3, 2, 6, 7], famille: 0 },
                    { s: [0, 3, 7, 4], famille: unique ? 0 : 1 },
                    { s: [1, 2, 6, 5], famille: unique ? 0 : 1 },
                    { s: [0, 1, 2, 3], famille: unique ? 0 : 2 },
                    { s: [4, 5, 6, 7], famille: unique ? 0 : 2 },
                ],
            }, h);
            const P = (i) => projeter(sommets[i]);
            cote(unique ? 'arete' : 'longueur', { a: P(0), b: P(1) }, sc);
            cote('hauteur', { a: P(0), b: P(4) }, sc);
            cote('largeur', { a: P(1), b: P(2) }, sc);
            return sc;
        }
        case 'prisme_triangulaire': {
            const [a, b, c] = s.cotes;
            const px = (a * a + c * c - b * b) / (2 * a);
            const py = Math.sqrt(Math.max(0, c * c - px * px));
            const H = s.hauteur;
            const sommets = [-H / 2, H / 2].flatMap((z) => [
                v3(-a / 2, 0, z),
                v3(a / 2, 0, z),
                v3(-a / 2 + px, py, z),
            ]);
            const { rangs } = famillesDuPrisme(s.cotes);
            const sc = scenePolyedre({
                sommets,
                faces: [
                    { s: [0, 1, 2], famille: 0 },
                    { s: [3, 4, 5], famille: 0 },
                    { s: [0, 1, 4, 3], famille: 1 + rangs[0] },
                    { s: [1, 2, 5, 4], famille: 1 + rangs[1] },
                    { s: [2, 0, 3, 5], famille: 1 + rangs[2] },
                ],
            }, h);
            const P = (i) => projeter(sommets[i]);
            cote('cote_a', { a: P(0), b: P(1) }, sc);
            /* Ce côté est une arête INTÉRIEURE au dessin (la face de droite la borde) : sa cote s'écrit dans le triangle. */
            cote('cote_b', { a: P(1), b: P(2), interieure: true, surTirets: true }, sc);
            cote('cote_c', { a: P(2), b: P(0) }, sc);
            cote('hauteur', { a: P(1), b: P(4) }, sc);
            const vers = (de, a2) => ({ x: a2.x - de.x, y: a2.y - de.y });
            /* L'angle droit de la base, marqué sur la base de devant. */
            const droit = s.angleDroit === true ? angleDroitDeLaBase(s.cotes) : null;
            if (droit !== null) {
                sc.equerres.push({
                    coin: P(droit),
                    u: vers(P(droit), P((droit + 1) % 3)),
                    v: vers(P(droit), P((droit + 2) % 3)),
                });
            }
            /* La hauteur de la base : du sommet opposé au premier côté, à son pied, en tirets. */
            if (h.cote('hauteur_base') !== null) {
                const pied = projeter(v3(-a / 2 + px, 0, -H / 2));
                sc.equerres.push({ coin: pied, u: vers(pied, P(2)), v: vers(pied, P(1)) });
                cote('hauteur_base', { a: P(2), b: pied, trace: 'cache', interieure: true }, sc);
                /* La lettre de la base est écrite en son centre — sur la hauteur, pour un triangle
                   isocèle. Elle passe dans la plus grande des deux moitiés que la hauteur sépare. */
                const centre = {
                    x: (P(0).x + P(1).x + P(2).x) / 3,
                    y: (P(0).y + P(1).y + P(2).y) / 3,
                };
                const coin = px >= a / 2 ? P(0) : P(1);
                const lettre = sc.lettres.find((l) => Math.hypot(l.p.x - centre.x, l.p.y - centre.y) < 1e-6);
                if (lettre !== undefined) {
                    lettre.p = { x: (coin.x + pied.x + P(2).x) / 3, y: (coin.y + pied.y + P(2).y) / 3 };
                }
            }
            return sc;
        }
        case 'pyramide': {
            const d = s.cote / 2;
            const sommets = [
                v3(-d, 0, -d),
                v3(d, 0, -d),
                v3(d, 0, d),
                v3(-d, 0, d),
                v3(0, hauteurDePyramide(s), 0),
            ];
            const sc = scenePolyedre({
                sommets,
                faces: [
                    { s: [0, 1, 2, 3], famille: 0 },
                    /* La face de devant porte la hauteur et l'apothème : sa lettre est sur la face de droite. */
                    { s: [0, 1, 4], famille: 1, muette: true },
                    { s: [1, 2, 4], famille: 1 },
                    { s: [2, 3, 4], famille: 1 },
                    { s: [3, 0, 4], famille: 1 },
                ],
            }, h);
            const P = (i) => projeter(sommets[i]);
            const pied = projeter(v3(0, 0, 0));
            const milieuDevant = projeter(v3(0, 0, -d));
            const vecteur = (de, vers) => ({ x: vers.x - de.x, y: vers.y - de.y });
            cote('cote', { a: P(0), b: P(1) }, sc);
            /* La hauteur et l'apothème sont DANS le dessin, serrés entre des arêtes.
               Constat de l'auteur de `MAT-2-SOLIDE` : « 13 cm », écrit entre l'arête
               latérale et l'apothème, se lisait comme la mesure de l'arête ; « 12 cm »
               était traversé par une arête. Chaque cote est donc à MI-LONGUEUR de son
               segment — jamais au niveau de la base, jamais au-dessus du sommet —,
               contre lui si elle y tient, sinon hors du solide avec un trait de
               rappel ; et l'angle droit est marqué à son pied. Cotés ENSEMBLE,
               l'apothème part à gauche et la hauteur à droite : la face de devant
               ne peut pas porter les deux écritures. */
            const ensemble = h.cote('hauteur') !== null && h.cote('apotheme') !== null;
            if (h.cote('hauteur') !== null) {
                sc.equerres.push({ coin: pied, u: vecteur(pied, P(4)), v: vecteur(pied, milieuDevant) });
            }
            cote('hauteur', { a: P(4), b: pied, trace: 'cache', interieure: ensemble ? 1 : true }, sc);
            if (h.cote('apotheme') !== null) {
                sc.equerres.push({
                    coin: milieuDevant,
                    u: vecteur(milieuDevant, P(4)),
                    v: vecteur(milieuDevant, P(1)),
                });
            }
            cote('apotheme', { a: P(4), b: milieuDevant, trace: 'plein', interieure: ensemble ? -1 : true }, sc);
            return sc;
        }
        case 'cylindre': {
            const sc = sceneVide();
            const { rayon: r, hauteur: H } = s;
            const devantBas = arc(r, 0, PHI - Math.PI / 2, PHI + Math.PI / 2);
            const derriereBas = arc(r, 0, PHI + Math.PI / 2, PHI + (3 * Math.PI) / 2);
            const derriereHaut = arc(r, H, PHI + Math.PI / 2, PHI + (3 * Math.PI) / 2);
            const haut = arc(r, H, 0, 2 * Math.PI, rond, ctx.dessus === undefined ? 1 : 3);
            sc.faces.push({ pts: [...devantBas, ...derriereHaut], surface: h.surface(1) });
            sc.faces.push({ pts: haut, surface: h.surface(0) });
            sc.pleins.push(devantBas);
            /* Le bord du dessus. Sous un autre solide (tranche T37) : ce que celui-ci en
               cache passe en tirets ; recouvert bord à bord, c'est le solide posé qui le trace. */
            if (ctx.dessus === undefined)
                sc.pleins.push(haut);
            else if (!ctx.dessus.entier) {
                const bord = departager(haut, ctx.dessus.cache);
                sc.pleins.push(...bord.pleins);
                sc.caches.push(...bord.caches);
            }
            sc.pleins.push([devantBas[0], derriereHaut[derriereHaut.length - 1]]);
            sc.pleins.push([devantBas[devantBas.length - 1], derriereHaut[0]]);
            sc.caches.push(derriereBas);
            const lettreBase = h.lettre(0);
            const lettreCote = h.lettre(1);
            if (lettreBase !== null)
                sc.lettres.push({ p: rond(v3(-r / 2, H, 0)), t: lettreBase });
            if (lettreCote !== null)
                sc.lettres.push({ p: rond(v3(0, H / 2, -r)), t: lettreCote });
            /* Le rayon se lit sur le disque du dessus. Quand un autre solide y est posé
               (tranche T37), il se lit sur le disque du DESSOUS, en tirets : le rayon du
               cylindre et celui du solide posé ne sont plus au bout du même trait. */
            const y = ctx.dessus === undefined ? H : 0;
            const trace = ctx.dessus === undefined ? 'plein' : 'cache';
            cote('rayon', { a: rond(v3(0, y, 0)), b: rond(v3(r, y, 0)), trace, vers: { x: 1, y: 0 }, ou: 1 }, sc);
            cote('diametre', { a: rond(v3(-r, y, 0)), b: rond(v3(r, y, 0)), trace, vers: { x: 1, y: 0 }, ou: 1 }, sc);
            /* La hauteur se lit sur la génératrice du bord droit. */
            const bords = [devantBas[0], devantBas[devantBas.length - 1]];
            const droit = bords[0].x > bords[1].x ? 0 : 1;
            const pied = bords[droit];
            cote('hauteur', { a: pied, b: { x: pied.x, y: pied.y + H } }, sc);
            return sc;
        }
        case 'cone': {
            const sc = sceneVide();
            const { rayon: r, hauteur: H } = s;
            const vue = ctx.vue ?? VUE_RONDE;
            /* Pointe en haut, base en bas — ou l'inverse (`renverse` : le bas d'un cornet, d'une toupie). */
            const renverse = s.renverse === true;
            const yBase = renverse ? H : 0;
            const { A, C, devant, derriere } = contourDeCone(r, yBase, renverse ? 0 : H, vue);
            sc.faces.push({ pts: [A, ...devant], surface: h.surface(1) });
            sc.pleins.push([A, devant[0]], [A, devant[devant.length - 1]]);
            if (!renverse) {
                sc.pleins.push(devant);
                sc.caches.push(derriere);
            }
            else {
                /* La base est le DESSUS : on la voit en entier, sauf ce que le solide posé en cache. */
                const tour = arc(r, yBase, 0, 2 * Math.PI, vue.P, 3);
                sc.faces.push({ pts: tour, surface: h.surface(0) });
                if (ctx.dessus === undefined)
                    sc.pleins.push(tour);
                else if (!ctx.dessus.entier) {
                    const bord = departager(tour, ctx.dessus.cache);
                    sc.pleins.push(...bord.pleins);
                    sc.caches.push(...bord.caches);
                }
            }
            const lettre = h.lettre(1);
            if (lettre !== null)
                sc.lettres.push({ p: rond(v3(r * 0.42, H * 0.2, -r * 0.3)), t: lettre });
            /* LES COTES (tranche T37). Constats de l'auteur de `MAT-3-SOL` sur un cône posé
               sur un cylindre : la hauteur s'écrivait à cheval sur la génératrice, le rayon
               sur l'ellipse de la base. Chacune garde sa place d'USAGE quand elle y tient
               sans toucher un autre trait ; sinon le rayon s'écrit au bout de son trait,
               hors du solide ; sinon la cote passe contre son segment, à mi-longueur (celle
               du rayon peut alors passer sur l'arc caché de la base, jamais sur un trait
               plein), ou hors du solide avec un trait de rappel. La hauteur est à
               GAUCHE de son trait, son angle droit marqué au centre ; l'apothème est à
               DROITE de la génératrice, dehors : elles ne peuvent pas se confondre. */
            const bout = vue.P(v3(r, yBase, 0));
            const sousLeTrait = { x: 0, y: 1 };
            cote('rayon', {
                a: C,
                b: bout,
                trace: 'cache',
                vers: sousLeTrait,
                interieure: true,
                auBout: true,
                surTirets: true,
            }, sc);
            cote('diametre', {
                a: vue.P(v3(-r, yBase, 0)),
                b: bout,
                trace: 'cache',
                vers: sousLeTrait,
                interieure: true,
                auBout: true,
                surTirets: true,
            }, sc);
            if (h.cote('hauteur') !== null) {
                sc.equerres.push({ coin: C, u: { x: A.x - C.x, y: A.y - C.y }, v: { x: 1, y: 0 } });
            }
            cote('hauteur', {
                a: A,
                b: C,
                trace: 'cache',
                vers: { x: -1, y: 0 },
                ou: 0.72,
                interieure: true,
                rappel: -1,
            }, sc);
            const bords = [devant[0], devant[devant.length - 1]];
            const droit = bords[0].x > bords[1].x ? bords[0] : bords[1];
            /* L'apothème : contre la génératrice de droite, DEHORS. Sur un petit cône posé,
               cette place est sur le bord du solide qui le porte : la cote s'écarte alors
               du même côté, avec son trait de rappel. */
            const [gx, gy] = [droit.x - A.x, droit.y - A.y];
            const raide = Math.abs(gy) >= Math.abs(gx);
            const dehors = gy < 0 ? { x: -gy, y: gx } : { x: gy, y: -gx };
            cote('apotheme', {
                a: A,
                b: droit,
                vers: { x: dehors.x, y: -dehors.y },
                interieure: raide || renverse ? 1 : -1,
            }, sc);
            return sc;
        }
        case 'boule': {
            const sc = sceneVide();
            const r = s.rayon;
            const contour = Array.from({ length: 65 }, (_, i) => {
                const t = (i / 64) * 2 * Math.PI;
                return { x: r * Math.cos(t), y: r * Math.sin(t) };
            });
            sc.faces.push({ pts: contour, surface: h.surface(0) });
            sc.pleins.push(contour);
            /* L'équateur, aplati comme un cercle horizontal vu d'en haut : sa moitié de derrière est cachée. */
            const equateur = (t0, t1) => Array.from({ length: 25 }, (_, i) => {
                const t = t0 + ((t1 - t0) * i) / 24;
                return { x: r * Math.cos(t), y: -APLATI * r * Math.sin(t) };
            });
            sc.pleins.push(equateur(0, Math.PI));
            sc.caches.push(equateur(Math.PI, 2 * Math.PI));
            cote('rayon', { a: { x: 0, y: 0 }, b: { x: r, y: 0 }, trace: 'cache', vers: { x: 0, y: -1 } }, sc);
            cote('diametre', { a: { x: -r, y: 0 }, b: { x: r, y: 0 }, trace: 'cache', vers: { x: 0, y: -1 } }, sc);
            return sc;
        }
        case 'demi_boule': {
            /* La face plane est en y = 0 : le dôme est au-dessus, le bol en dessous. Comme
               pour la boule, le contour est un (demi-)cercle et la face plane une ellipse couchée. */
            const sc = sceneVide();
            const r = s.rayon;
            const bol = s.pose === 'bol';
            const demiCercle = Array.from({ length: 33 }, (_, i) => {
                const t = (i / 32) * Math.PI;
                return { x: r * Math.cos(t), y: (bol ? -1 : 1) * r * Math.sin(t) };
            });
            const devant = arc(r, 0, PHI - Math.PI / 2, PHI + Math.PI / 2);
            const derriere = arc(r, 0, PHI + Math.PI / 2, PHI + (3 * Math.PI) / 2);
            sc.pleins.push(demiCercle, devant);
            if (bol) {
                /* La face plane est le dessus : on la voit en entier. */
                sc.faces.push({ pts: [...demiCercle, ...[...derriere].reverse()], surface: h.surface(0) });
                sc.faces.push({ pts: [...devant, ...derriere], surface: h.surface(0) });
                sc.pleins.push(derriere);
            }
            else {
                sc.faces.push({ pts: [...demiCercle, ...devant], surface: h.surface(0) });
                sc.caches.push(derriere);
            }
            /* Le rayon : du centre au bord droit de la face plane — en tirets sous le dôme,
               en trait plein sur le dessus du bol. Sa cote est SOUS son trait, dans la face
               plane, si elle y tient loin de l'ellipse ; sinon au bout de son trait, dehors ;
               sinon elle a son trait de rappel. */
            const trace = bol ? 'plein' : 'cache';
            const sousLeTrait = { x: 0, y: 1 };
            cote('rayon', {
                a: { x: 0, y: 0 },
                b: { x: r, y: 0 },
                trace,
                vers: sousLeTrait,
                interieure: true,
                auBout: true,
            }, sc);
            cote('diametre', {
                a: { x: -r, y: 0 },
                b: { x: r, y: 0 },
                trace,
                vers: sousLeTrait,
                interieure: true,
                auBout: true,
            }, sc);
            return sc;
        }
    }
}
const rectangle = (x, y, l, h) => [
    { x, y },
    { x: x + l, y },
    { x: x + l, y: y + h },
    { x, y: y + h },
];
const disque = (cx, cy, r) => Array.from({ length: 64 }, (_, i) => {
    const t = (i / 64) * 2 * Math.PI;
    return { x: cx + r * Math.cos(t), y: cy + r * Math.sin(t) };
});
/**
 * Des faces à plat à la scène : une arête commune à deux faces est un PLI (en
 * pointillé — c'est là qu'on replie) ; une arête d'une seule face est le bord
 * du développement (en trait plein — c'est là qu'on découpe).
 */
function sceneAPlat(faces, h) {
    const sc = sceneVide();
    const cle = (p) => `${Math.round(p.x * 1e6)}:${Math.round(p.y * 1e6)}`;
    const aretes = new Map();
    for (const f of faces) {
        sc.faces.push({ pts: f.pts, surface: h.surface(f.famille) });
        const lettre = h.lettre(f.famille);
        if (lettre !== null) {
            const c = f.centre ?? {
                x: f.pts.reduce((s, p) => s + p.x, 0) / f.pts.length,
                y: f.pts.reduce((s, p) => s + p.y, 0) / f.pts.length,
            };
            sc.lettres.push({ p: c, t: lettre });
        }
        if (f.lisse === true) {
            sc.pleins.push([...f.pts, f.pts[0]]);
            continue;
        }
        f.pts.forEach((a, i) => {
            const b = f.pts[(i + 1) % f.pts.length];
            const [ka, kb] = [cle(a), cle(b)];
            const k = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
            aretes.set(k, { a, b, n: (aretes.get(k)?.n ?? 0) + 1 });
        });
    }
    for (const e of aretes.values())
        (e.n > 1 ? sc.caches : sc.pleins).push([e.a, e.b]);
    return sc;
}
function developpementDe(s, h) {
    const cote = (g, c, sc) => {
        const t = h.cote(g);
        if (t !== null)
            sc.cotes.push({ ...c, texte: t });
    };
    switch (s.forme) {
        case 'cube': {
            const a = s.arete;
            const cases = s.patron ?? PATRON_EN_CROIX;
            const sc = sceneAPlat(cases.map((c) => ({ pts: rectangle(c[0] * a, c[1] * a, a, a), famille: 0 })), h);
            /* La cote se pose sur le bord gauche de la case la plus à gauche : il est libre. */
            const g = [...cases].sort((p, q) => p[0] - q[0] || p[1] - q[1])[0];
            cote('arete', { a: { x: g[0] * a, y: g[1] * a }, b: { x: g[0] * a, y: (g[1] + 1) * a } }, sc);
            return sc;
        }
        case 'pave': {
            const { longueur: L, largeur: l, hauteur: H } = s;
            const sc = sceneAPlat([
                { pts: rectangle(0, 0, L, H), famille: 0 },
                { pts: rectangle(L, 0, l, H), famille: 1 },
                { pts: rectangle(L + l, 0, L, H), famille: 0 },
                { pts: rectangle(2 * L + l, 0, l, H), famille: 1 },
                { pts: rectangle(0, H, L, l), famille: 2 },
                { pts: rectangle(0, -l, L, l), famille: 2 },
            ], h);
            cote('longueur', { a: { x: 0, y: H + l }, b: { x: L, y: H + l } }, sc);
            cote('largeur', { a: { x: 0, y: H }, b: { x: 0, y: H + l } }, sc);
            cote('hauteur', { a: { x: 2 * L + 2 * l, y: 0 }, b: { x: 2 * L + 2 * l, y: H } }, sc);
            return sc;
        }
        case 'prisme_triangulaire': {
            const [a, b, c] = s.cotes;
            const H = s.hauteur;
            const { rangs } = famillesDuPrisme(s.cotes);
            /* Une base au-dessus du premier rectangle, l'autre sous le deuxième : chaque côté garde un bord libre où se coter. */
            const px = (a * a + c * c - b * b) / (2 * a);
            const py = Math.sqrt(Math.max(0, c * c - px * px));
            const qx = (b * b + a * a - c * c) / (2 * b);
            const qy = Math.sqrt(Math.max(0, a * a - qx * qx));
            const sc = sceneAPlat([
                { pts: rectangle(0, 0, a, H), famille: 1 + rangs[0] },
                { pts: rectangle(a, 0, b, H), famille: 1 + rangs[1] },
                { pts: rectangle(a + b, 0, c, H), famille: 1 + rangs[2] },
                {
                    pts: [
                        { x: 0, y: H },
                        { x: a, y: H },
                        { x: px, y: H + py },
                    ],
                    famille: 0,
                    /* La hauteur de la base, si elle est cotée, passe par le centre : la lettre s'écarte. */
                    ...(h.cote('hauteur_base') !== null
                        ? {
                            centre: {
                                x: ((px >= a / 2 ? 0 : a) + 2 * px) / 3,
                                y: H + py / 3,
                            },
                        }
                        : {}),
                },
                {
                    pts: [
                        { x: a, y: 0 },
                        { x: a + b, y: 0 },
                        { x: a + qx, y: -qy },
                    ],
                    famille: 0,
                },
            ], h);
            cote('cote_a', { a: { x: 0, y: 0 }, b: { x: a, y: 0 } }, sc);
            cote('cote_b', { a: { x: a, y: H }, b: { x: a + b, y: H } }, sc);
            cote('cote_c', { a: { x: a + b, y: H }, b: { x: a + b + c, y: H } }, sc);
            cote('hauteur', { a: { x: 0, y: 0 }, b: { x: 0, y: H } }, sc);
            /* La base du haut porte l'angle droit et la hauteur de la base : ses sommets 0, 1, 2. */
            const B = [
                { x: 0, y: H },
                { x: a, y: H },
                { x: px, y: H + py },
            ];
            const vers = (de, a2) => ({ x: a2.x - de.x, y: a2.y - de.y });
            const droit = s.angleDroit === true ? angleDroitDeLaBase(s.cotes) : null;
            if (droit !== null) {
                const coin = B[droit];
                sc.equerres.push({
                    coin,
                    u: vers(coin, B[(droit + 1) % 3]),
                    v: vers(coin, B[(droit + 2) % 3]),
                });
            }
            if (h.cote('hauteur_base') !== null) {
                const pied = { x: px, y: H };
                sc.equerres.push({ coin: pied, u: { x: 0, y: 1 }, v: { x: 1, y: 0 } });
                cote('hauteur_base', { a: B[2], b: pied, trace: 'cache', interieure: true, surTirets: true }, sc);
            }
            return sc;
        }
        case 'pyramide': {
            const c = s.cote;
            const ap = apothemeDe(s);
            const sc = sceneAPlat([
                { pts: rectangle(0, 0, c, c), famille: 0 },
                {
                    pts: [
                        { x: 0, y: 0 },
                        { x: c, y: 0 },
                        { x: c / 2, y: -ap },
                    ],
                    famille: 1,
                },
                {
                    pts: [
                        { x: c, y: 0 },
                        { x: c, y: c },
                        { x: c + ap, y: c / 2 },
                    ],
                    famille: 1,
                    /* L'apothème traverse ce triangle : sa lettre est écrite sous le trait. */
                    centre: { x: c + ap * 0.28, y: c / 2 - c * 0.2 },
                },
                {
                    pts: [
                        { x: c, y: c },
                        { x: 0, y: c },
                        { x: c / 2, y: c + ap },
                    ],
                    famille: 1,
                },
                {
                    pts: [
                        { x: 0, y: c },
                        { x: 0, y: 0 },
                        { x: -ap, y: c / 2 },
                    ],
                    famille: 1,
                },
            ], h);
            /* Les quatre côtés du carré sont des plis : la cote s'écrit DANS le carré. */
            cote('cote', { a: { x: 0, y: 0 }, b: { x: c, y: 0 }, vers: { x: 0, y: -1 } }, sc);
            if (h.cote('apotheme') !== null) {
                sc.equerres.push({ coin: { x: c, y: c / 2 }, u: { x: 1, y: 0 }, v: { x: 0, y: 1 } });
            }
            cote('apotheme', {
                a: { x: c, y: c / 2 },
                b: { x: c + ap, y: c / 2 },
                trace: 'cache',
                vers: { x: 0, y: -1 },
                ou: 0.3,
                libre: true,
            }, sc);
            return sc;
        }
        case 'cylindre': {
            const { rayon: r, hauteur: H } = s;
            const l = 2 * Math.PI * r;
            const sc = sceneAPlat([
                { pts: rectangle(0, 0, l, H), famille: 1 },
                {
                    pts: disque(l / 2, H + r, r),
                    famille: 0,
                    lisse: true,
                    centre: { x: l / 2 - r / 2, y: H + r - r / 3 },
                },
                { pts: disque(l / 2, -r, r), famille: 0, lisse: true, centre: { x: l / 2, y: -r } },
            ], h);
            cote('rayon', {
                a: { x: l / 2, y: H + r },
                b: { x: l / 2 + r, y: H + r },
                trace: 'plein',
                vers: { x: 1, y: 0 },
                ou: 1,
            }, sc);
            cote('diametre', {
                a: { x: l / 2 - r, y: H + r },
                b: { x: l / 2 + r, y: H + r },
                trace: 'plein',
                vers: { x: 1, y: 0 },
                ou: 1,
            }, sc);
            cote('hauteur', { a: { x: 0, y: 0 }, b: { x: 0, y: H } }, sc);
            /* La longueur du rectangle EST la circonférence de la base : c'est tout le développement. */
            /* Le long du grand côté d'EN BAS, à droite du disque : en haut, à côté du
               disque qui porte le rayon, les deux écritures se chevauchaient dès que
               le rayon est petit. */
            cote('circonference', { a: { x: l / 2 + r, y: 0 }, b: { x: l, y: 0 }, vers: { x: 0, y: 1 }, ou: 0.85 }, sc);
            return sc;
        }
        case 'cone': {
            const { rayon: r } = s;
            const g = apothemeDe(s);
            const angle = (2 * Math.PI * r) / g;
            const t0 = -Math.PI / 2 - angle / 2;
            const n = Math.max(8, Math.ceil((angle / (2 * Math.PI)) * 64));
            const bord = Array.from({ length: n + 1 }, (_, i) => {
                const t = t0 + (angle * i) / n;
                return { x: g * Math.cos(t), y: g * Math.sin(t) };
            });
            const sc = sceneAPlat([
                {
                    pts: [{ x: 0, y: 0 }, ...bord],
                    famille: 1,
                    lisse: true,
                    centre: { x: 0, y: -0.6 * g },
                },
                {
                    pts: disque(0, -g - r, r),
                    famille: 0,
                    lisse: true,
                    centre: { x: -r / 2, y: -g - r - r / 3 },
                },
            ], h);
            /* Dehors, contre le bord gauche du secteur ; quand le secteur est presque un
               disque (cône très plat), l'encoche est trop étroite et la cote passe dedans. */
            cote('apotheme', {
                a: { x: 0, y: 0 },
                b: bord[0],
                ...(angle > 1.6 * Math.PI ? { libre: true } : {}),
            }, sc);
            /* L'ARC du secteur a la longueur du tour de la base (tranche T37) : sa cote
               s'écrit le long de l'arc, DEHORS, au quart de l'arc — loin du disque, qui
               le touche en son milieu. */
            const quart = Math.max(1, Math.round(n * 0.22));
            const surLArc = bord[quart];
            cote('circonference', {
                a: bord[quart - 1],
                b: bord[quart + 1],
                vers: { x: surLArc.x, y: -surLArc.y },
            }, sc);
            cote('rayon', {
                a: { x: 0, y: -g - r },
                b: { x: r, y: -g - r },
                trace: 'plein',
                vers: { x: 1, y: 0 },
                ou: 1,
            }, sc);
            cote('diametre', {
                a: { x: -r, y: -g - r },
                b: { x: r, y: -g - r },
                trace: 'plein',
                vers: { x: 1, y: 0 },
                ou: 1,
            }, sc);
            return sc;
        }
        case 'boule':
        case 'demi_boule':
            return sceneVide();
    }
}
/* ───────────────────────────────── le dessin ───────────────────────────────── */
const n1 = (v) => (Math.round(v * 10) / 10).toString();
const pt = (p) => `${n1(p.x)} ${n1(p.y)}`;
const ligne = (pts) => pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${pt(p)}`).join(' ');
/** Une ligne en POINTILLÉ : des tirets de 6 pour 5 de blanc, comptés le long de la ligne. */
function pointille(pts) {
    const morceaux = [];
    let reste = 6;
    let plein = true;
    for (let i = 0; i + 1 < pts.length; i += 1) {
        let a = pts[i];
        const b = pts[i + 1];
        let l = Math.hypot(b.x - a.x, b.y - a.y);
        while (l > 1e-9) {
            const pas = Math.min(reste, l);
            const c = { x: a.x + ((b.x - a.x) * pas) / l, y: a.y + ((b.y - a.y) * pas) / l };
            if (plein)
                morceaux.push(`M${pt(a)} L${pt(c)}`);
            a = c;
            l -= pas;
            reste -= pas;
            if (reste <= 1e-9) {
                plein = !plein;
                reste = plein ? 6 : 5;
            }
        }
    }
    return morceaux.join(' ');
}
/** Le corps d'une cote, et le côté d'une marque d'angle droit, en points de la toile. */
const TAILLE_COTE = 14;
const EQUERRE = 7;
/** Une cote « contre » son segment intérieur doit garder cet écart à tout autre trait ; sinon, trait de rappel. */
const ECART_COTE_CONTRE = 4;
/** Du bord du solide au bout du trait de rappel. */
const ECART_RAPPEL = 10;
/** Ce qu'une écriture peut prendre au-dessus du cadre (le titre d'une vue y est déjà). */
const HAUT_LIBRE = 2;
const ecrit = (x, y, t, ancre, fort, taille = TAILLE_COTE) => ({
    type: 'texte',
    x,
    y,
    texte: t,
    taille,
    ancre,
    police: fort ? 'etiquette_forte' : 'etiquette',
    ton: fort ? 'encre' : 'doux',
});
/** Peint une scène dans son cadre : à l'échelle, centrée, les cotes écrites vers le dehors. */
function peindre(sc, cadre, traces) {
    const pts = [...sc.faces.flatMap((f) => f.pts), ...sc.pleins.flat(), ...sc.caches.flat()];
    if (pts.length === 0)
        return;
    const xs = pts.map((p) => p.x);
    const ys = pts.map((p) => p.y);
    const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
    const MARGE_X = 64;
    const MARGE_Y = 30;
    const k = Math.min((cadre.largeur - 2 * MARGE_X) / Math.max(x1 - x0, 1e-9), (cadre.hauteur - 2 * MARGE_Y) / Math.max(y1 - y0, 1e-9));
    const ox = cadre.x + (cadre.largeur - (x1 - x0) * k) / 2;
    const oy = cadre.y + (cadre.hauteur - (y1 - y0) * k) / 2;
    const E = (p) => ({ x: ox + (p.x - x0) * k, y: oy + (y1 - p.y) * k });
    const centre = E({ x: (x0 + x1) / 2, y: (y0 + y1) / 2 });
    for (const f of sc.faces) {
        traces.push({
            type: 'polygone',
            points: f.pts.map((p) => [E(p).x, E(p).y]),
            surface: f.surface,
        });
    }
    for (const l of sc.caches)
        traces.push({ type: 'chemin', d: pointille(l.map(E)), trait: 'doux' });
    for (const l of sc.pleins)
        traces.push({ type: 'chemin', d: ligne(l.map(E)), trait: 'encre' });
    /* Les marques d'angle droit : une équerre de taille fixe à l'écran. */
    for (const e of sc.equerres) {
        const coin = E(e.coin);
        const dir = (d) => {
            const q = E({ x: e.coin.x + d.x, y: e.coin.y + d.y });
            const l = Math.max(Math.hypot(q.x - coin.x, q.y - coin.y), 1e-9);
            return { x: ((q.x - coin.x) / l) * EQUERRE, y: ((q.y - coin.y) / l) * EQUERRE };
        };
        const [u, v] = [dir(e.u), dir(e.v)];
        traces.push({
            type: 'chemin',
            d: ligne([
                { x: coin.x + u.x, y: coin.y + u.y },
                { x: coin.x + u.x + v.x, y: coin.y + u.y + v.y },
                { x: coin.x + v.x, y: coin.y + v.y },
            ]),
            trait: 'encre',
        });
    }
    /* Ce qu'une cote « libre » doit éviter : toute arête, tout segment coté, toute lettre. */
    const segments = [];
    for (const l of [...sc.pleins, ...sc.caches]) {
        for (let i = 1; i < l.length; i += 1)
            segments.push([E(l[i - 1]), E(l[i])]);
    }
    const lettres = sc.lettres.map((l) => E(l.p));
    const auSegment = (q, [a, b]) => {
        const l2 = (b.x - a.x) ** 2 + (b.y - a.y) ** 2;
        const t = l2 < 1e-12 ? 0 : ((q.x - a.x) * (b.x - a.x) + (q.y - a.y) * (b.y - a.y)) / l2;
        const c = Math.max(0, Math.min(1, t));
        return Math.hypot(q.x - (a.x + c * (b.x - a.x)), q.y - (a.y + c * (b.y - a.y)));
    };
    /* Les écritures déjà posées : une cote ne s'écrit pas sur une autre. Les cotes à place
       FIXE sont posées d'abord, les cotes « libres » ensuite, dans leur ordre. */
    const posees = [];
    const cherchee = (x) => x.libre === true || x.interieure !== undefined;
    for (const c of [...sc.cotes.filter((x) => !cherchee(x)), ...sc.cotes.filter(cherchee)]) {
        const [a, b] = [E(c.a), E(c.b)];
        if (c.trace === 'plein')
            traces.push({ type: 'chemin', d: ligne([a, b]), trait: 'encre' });
        if (c.trace === 'cache')
            traces.push({ type: 'chemin', d: pointille([a, b]), trait: 'doux' });
        const l = Math.max(Math.hypot(b.x - a.x, b.y - a.y), 1e-9);
        const n = { x: -(b.y - a.y) / l, y: (b.x - a.x) / l };
        /** L'écriture posée en `ou`, du côté `vers` : son ancre, sa ligne de base, et sa boîte. */
        const pose = (ou, vers) => {
            const m = { x: a.x + (b.x - a.x) * ou, y: a.y + (b.y - a.y) * ou };
            const lv = Math.max(Math.hypot(vers.x, vers.y), 1e-9);
            const u = { x: vers.x / lv, y: vers.y / lv };
            const ancre = u.x > 0.35 ? 'debut' : u.x < -0.35 ? 'fin' : 'milieu';
            const x = m.x + u.x * (c.pres === true ? 6 : 10);
            const y = m.y + u.y * 13 + 5;
            const w = c.texte.length * TAILLE_COTE * 0.56;
            const gauche = ancre === 'debut' ? x : ancre === 'fin' ? x - w : x - w / 2;
            return {
                ancre,
                x,
                y,
                boite: { x0: gauche, x1: gauche + w, y0: y - TAILLE_COTE * 0.78, y1: y + 2 },
            };
        };
        const ouVoulu = c.ou ?? 0.5;
        let versVoulu = c.vers;
        if (versVoulu === undefined) {
            /* Vers le dehors : la normale au segment qui s'éloigne du centre du dessin. */
            const m = { x: a.x + (b.x - a.x) * ouVoulu, y: a.y + (b.y - a.y) * ouVoulu };
            versVoulu = n.x * (m.x - centre.x) + n.y * (m.y - centre.y) >= 0 ? n : { x: -n.x, y: -n.y };
        }
        let choisie = pose(ouVoulu, versVoulu);
        if (c.libre === true) {
            /* Les autres traits : les arêtes, et les segments tracés des AUTRES cotes. Les
               arêtes qui portent ce segment même (une cote sur une arête) ne comptent pas. */
            const autres = [
                ...segments.filter(([p, q]) => !(auSegment(p, [a, b]) < 0.5 && auSegment(q, [a, b]) < 0.5)),
                ...sc.cotes
                    .filter((x) => x !== c && x.trace !== undefined)
                    .map((x) => [E(x.a), E(x.b)]),
            ];
            const note = (q, ou, vers) => {
                const { x0, x1, y0, y1 } = q.boite;
                const xm = (x0 + x1) / 2;
                const ym = (y0 + y1) / 2;
                const sondes = [x0, xm, x1].flatMap((x) => [y0, ym, y1].map((y) => ({ x, y })));
                /* L'écart de la boîte au trait le plus proche, et à la lettre la plus proche. */
                let ecart = Infinity;
                for (const sg of autres)
                    for (const p of sondes)
                        ecart = Math.min(ecart, auSegment(p, sg));
                for (const le of lettres) {
                    const dx = Math.max(x0 - le.x, 0, le.x - x1);
                    const dy = Math.max(y0 - le.y + 6, 0, le.y - 6 - y1);
                    ecart = Math.min(ecart, Math.hypot(dx, dy) - 9);
                }
                /* Une autre écriture : les deux boîtes ne se touchent pas. */
                for (const o of posees) {
                    const dx = Math.max(o.x0 - x1, 0, x0 - o.x1);
                    const dy = Math.max(o.y0 - y1, 0, y0 - o.y1);
                    ecart = Math.min(ecart, Math.hypot(dx, dy) - 4);
                }
                /* Un trait qui TRAVERSE la boîte : ses sondes sont de part et d'autre. */
                const traversee = autres.some(([p, q2]) => {
                    const cote = (s2) => Math.sign((q2.x - p.x) * (s2.y - p.y) - (q2.y - p.y) * (s2.x - p.x));
                    const signes = new Set(sondes.map(cote));
                    const dansLaPortee = sondes.some((s2) => auSegment(s2, [p, q2]) < Math.hypot(x1 - x0, y1 - y0));
                    return (signes.has(1) &&
                        signes.has(-1) &&
                        dansLaPortee &&
                        auSegment({ x: xm, y: ym }, [p, q2]) < (y1 - y0) / 2 + 1);
                });
                const horsCadre = x0 < cadre.x + 2 ||
                    x1 > cadre.x + cadre.largeur - 2 ||
                    y0 < cadre.y + 2 ||
                    y1 > cadre.y + cadre.hauteur - 2;
                /* Au-delà de 24 points, plus loin n'est pas mieux : la préférence départage. */
                const prefere = (vers.x * versVoulu.x + vers.y * versVoulu.y > 0 ? 0 : 1) + Math.abs(ou - ouVoulu) * 2;
                return Math.min(ecart, 24) - prefere - (traversee ? 100 : 0) - (horsCadre ? 200 : 0);
            };
            let meilleure = note(choisie, ouVoulu, versVoulu);
            const essayer = (ou, vers) => {
                const q = pose(ou, vers);
                const v = note(q, ou, vers);
                if (v > meilleure + 1e-9) {
                    meilleure = v;
                    choisie = q;
                }
            };
            for (const vers of [n, { x: -n.x, y: -n.y }]) {
                for (let ou = 0.2; ou <= 0.901; ou += 0.1)
                    essayer(ou, vers);
            }
            /* Au bout du segment, dans son prolongement : quand ses deux côtés sont pris. */
            const t = { x: (b.x - a.x) / l, y: (b.y - a.y) / l };
            essayer(1, t);
            essayer(0, { x: -t.x, y: -t.y });
        }
        if (c.interieure !== undefined) {
            /* À MI-LONGUEUR. Les autres traits : toute arête qui n'est pas ce segment,
               et les segments tracés des autres cotes. */
            const m = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
            const autres = [
                ...segments.filter(([p, q]) => !(auSegment(p, [a, b]) < 0.5 && auSegment(q, [a, b]) < 0.5)),
                ...sc.cotes
                    .filter((x) => x !== c && x.trace !== undefined)
                    .map((x) => [E(x.a), E(x.b)]),
            ];
            const w = c.texte.length * TAILLE_COTE * 0.56;
            /* Les traits PLEINS seulement : une écriture peut passer sur une arête cachée en
               tirets (elle est derrière), pas sur une arête visible. */
            const estPlein = (sg) => sc.pleins.some((l) => l.some((_, i) => i > 0 &&
                auSegment(sg[0], [E(l[i - 1]), E(l[i])]) < 0.5 &&
                auSegment(sg[1], [E(l[i - 1]), E(l[i])]) < 0.5));
            const pleinsAutres = [
                ...segments
                    .filter(estPlein)
                    .filter(([p, q]) => !(auSegment(p, [a, b]) < 0.5 && auSegment(q, [a, b]) < 0.5)),
                ...sc.cotes
                    .filter((x) => x !== c && x.trace !== undefined)
                    .map((x) => [E(x.a), E(x.b)]),
            ];
            /** L'écart d'une boîte au trait, à la lettre ou à l'écriture la plus proche. */
            const libreAutour = (bt, traits = autres, strict = false) => {
                const xm = (bt.x0 + bt.x1) / 2;
                const ym = (bt.y0 + bt.y1) / 2;
                const sondes = [bt.x0, xm, bt.x1].flatMap((x) => [bt.y0, ym, bt.y1].map((y) => ({ x, y })));
                let ecart = Infinity;
                for (const sg of traits)
                    for (const q of sondes)
                        ecart = Math.min(ecart, auSegment(q, sg));
                /* Un trait raide peut TRAVERSER la boîte entre deux sondes (la génératrice d'un
                   cône étroit, tranche T37) : ses propres points sont alors dans la boîte. */
                if (strict) {
                    for (const [u, w] of traits) {
                        for (let i = 0; i <= 8 && ecart > 0; i += 1) {
                            const q = { x: u.x + ((w.x - u.x) * i) / 8, y: u.y + ((w.y - u.y) * i) / 8 };
                            const dx = Math.max(bt.x0 - q.x, 0, q.x - bt.x1);
                            const dy = Math.max(bt.y0 - q.y, 0, q.y - bt.y1);
                            ecart = Math.min(ecart, Math.hypot(dx, dy));
                        }
                    }
                }
                for (const le of lettres) {
                    const dx = Math.max(bt.x0 - le.x, 0, le.x - bt.x1);
                    const dy = Math.max(bt.y0 - le.y + 6, 0, le.y - 6 - bt.y1);
                    ecart = Math.min(ecart, Math.hypot(dx, dy) - 9);
                }
                for (const o of posees) {
                    const dx = Math.max(o.x0 - bt.x1, 0, bt.x0 - o.x1);
                    const dy = Math.max(o.y0 - bt.y1, 0, bt.y0 - o.y1);
                    ecart = Math.min(ecart, Math.hypot(dx, dy) - 4);
                }
                return ecart;
            };
            /* 1. Contre le segment, d'un côté ou de l'autre : là où il reste le plus de place. */
            /* Avec `rappel`, un seul côté est permis : celui où partirait le trait de rappel. */
            const contre = [n, { x: -n.x, y: -n.y }]
                .filter((vers) => c.rappel === undefined ||
                Math.sign(Math.abs(vers.x) >= Math.abs(vers.y) ? vers.x : vers.y) === c.rappel)
                .map((vers) => {
                const lv = Math.abs(vers.x) > 0.35 ? Math.sign(vers.x) : 0;
                const x = m.x + vers.x * 6;
                const y = m.y + vers.y * 13 + 5;
                const x0 = lv > 0 ? x : lv < 0 ? x - w : x - w / 2;
                const boite = { x0, x1: x0 + w, y0: y - TAILLE_COTE * 0.78, y1: y + 2 };
                const ancre = lv > 0 ? 'debut' : lv < 0 ? 'fin' : 'milieu';
                return {
                    x,
                    y,
                    ancre,
                    boite,
                    ecart: libreAutour(boite, c.surTirets === true ? pleinsAutres : autres, c.vers !== undefined),
                };
            })
                .sort((p, q) => q.ecart - p.ecart)[0];
            /* 0. La place d'usage (`vers`, `ou`), quand elle est dite et qu'elle est libre. */
            const usage = c.vers !== undefined ? pose(ouVoulu, versVoulu) : undefined;
            if (usage !== undefined && libreAutour(usage.boite, autres, true) >= ECART_COTE_CONTRE) {
                choisie = usage;
            }
            else if (c.interieure === true &&
                c.auBout === true &&
                libreAutour(pose(1, { x: b.x - a.x, y: b.y - a.y }).boite, autres, true) >=
                    ECART_COTE_CONTRE) {
                choisie = pose(1, { x: b.x - a.x, y: b.y - a.y });
            }
            else if (c.interieure === true &&
                contre !== undefined &&
                contre.ecart >= ECART_COTE_CONTRE) {
                choisie = contre;
            }
            else {
                /* 2. Hors du solide, à la hauteur du milieu, avec un trait de rappel. Le bord
                   du dessin, de ce côté : le dernier trait que coupe la demi-droite. */
                const sortie = (sens) => {
                    let loin = 0;
                    for (const [p, q] of [...segments, ...autres]) {
                        const dx = q.x - p.x;
                        const dy = q.y - p.y;
                        const det = sens.x * dy - sens.y * dx;
                        if (Math.abs(det) < 1e-9)
                            continue;
                        const t = ((p.x - m.x) * dy - (p.y - m.y) * dx) / det;
                        const u = ((p.x - m.x) * sens.y - (p.y - m.y) * sens.x) / det;
                        if (t > 0 && u >= -1e-6 && u <= 1 + 1e-6)
                            loin = Math.max(loin, t);
                    }
                    return loin;
                };
                /* Un segment plutôt vertical se rappelle à l'horizontale, et inversement. Le
                   trait part à plat, ou incliné de 30° : dans un solide très plat, le bord
                   est bien plus près en biais qu'à l'horizontale. L'écriture reste À LA
                   HAUTEUR de son segment — jamais au niveau de la base, jamais au-dessus
                   du sommet — ; le trait le plus COURT est gardé. */
                const horizontal = Math.abs(n.x) >= Math.abs(n.y);
                const [bas, haut] = horizontal
                    ? [Math.min(a.y, b.y), Math.max(a.y, b.y)]
                    : [Math.min(a.x, b.x), Math.max(a.x, b.x)];
                const COS = Math.cos(Math.PI / 6);
                const sensDe = (signe, biais) => horizontal
                    ? { x: signe * (biais === 0 ? 1 : COS), y: biais * 0.5 }
                    : { x: biais * 0.5, y: signe * (biais === 0 ? 1 : COS) };
                const sens = c.interieure !== true ? [c.interieure] : c.rappel !== undefined ? [c.rappel] : [-1, 1];
                const candidats = sens.flatMap((signe) => [0, -1, 1].flatMap((biais) => 
                /* Un peu plus loin du bord si l'écriture y touche encore un trait. */
                [0, 6, 14, 24].map((plus) => {
                    const d = sensDe(signe, biais);
                    const bout = sortie(d) + ECART_RAPPEL + plus;
                    const p = { x: m.x + d.x * bout, y: m.y + d.y * bout };
                    const x = horizontal ? p.x + signe * 3 : p.x;
                    const y = horizontal ? p.y + 5 : p.y + (signe > 0 ? TAILLE_COTE : -4);
                    const ancre = !horizontal
                        ? 'milieu'
                        : signe > 0
                            ? 'debut'
                            : 'fin';
                    const x0 = ancre === 'debut' ? x : ancre === 'fin' ? x - w : x - w / 2;
                    const boite = { x0, x1: x0 + w, y0: y - TAILLE_COTE * 0.78, y1: y + 2 };
                    const milieuBoite = horizontal
                        ? (boite.y0 + boite.y1) / 2
                        : (boite.x0 + boite.x1) / 2;
                    const dedans = boite.x0 >= cadre.x + 2 &&
                        boite.x1 <= cadre.x + cadre.largeur - 2 &&
                        boite.y0 >= cadre.y - HAUT_LIBRE &&
                        boite.y1 <= cadre.y + cadre.hauteur + 2 &&
                        milieuBoite >= bas - TAILLE_COTE / 2 &&
                        milieuBoite <= haut + TAILLE_COTE / 2 &&
                        libreAutour(boite, autres, c.vers !== undefined) >= 2;
                    /* À plat de préférence : le biais ne l'emporte que s'il est nettement plus court. */
                    return { x, y, ancre, boite, p, bout, dedans, cout: bout * (biais === 0 ? 0.6 : 1) };
                })));
                const rappel = candidats.sort((p, q) => Number(q.dedans) - Number(p.dedans) || p.cout - q.cout)[0];
                traces.push({ type: 'chemin', d: ligne([m, rappel.p]), trait: 'doux' });
                choisie = rappel;
            }
        }
        posees.push(choisie.boite);
        traces.push(ecrit(choisie.x, choisie.y, c.texte, choisie.ancre, false));
    }
    for (const l of sc.lettres) {
        const p = E(l.p);
        traces.push(ecrit(p.x, p.y + 6, l.t, 'milieu', true, 17));
    }
}
const L = LARGEUR_TOILE;
/**
 * Le dessin d'un solide illustré. Avec des griefs, rien n'est dessiné à moitié
 * — sauf le patron fautif d'une option, que `options` permet : il se dessine,
 * c'est sa raison d'être.
 */
export function resoudreSolide(d, options = {}) {
    const griefs = [...griefsDeSolide(d, options)];
    const traces = [];
    if (griefs.length > 0)
        return { largeur: L, hauteur: 120, decrit: d.decrit, traces, griefs };
    const s = d.solide;
    const unite = d.unite ?? '';
    const familles = d.faces === true ? famillesDeFaces(s, unite) : [];
    const habillage = (partie, nom) => ({
        surface: (f) => familles[f]?.surface ?? 'papier',
        lettre: (f) => familles[f]?.etiquette ?? null,
        cote: (g) => {
            const c = (d.cotes ?? []).find((x) => x.grandeur === g && x.partie === nom);
            if (c === undefined)
                return null;
            if (c.texte !== undefined)
                return c.texte;
            const v = valeurDeGrandeur(partie, g);
            const mesure = unite === '' ? fr(v) : `${fr(v)} ${unite}`;
            return g === 'circonference' && partie.forme === 'cylindre'
                ? `2π × ${unite === '' ? fr(partie.rayon) : `${fr(partie.rayon)} ${unite}`}`
                : mesure;
        },
    });
    const HAUT = 30;
    const CORPS = 290;
    const deux = d.vue === 'les_deux';
    if (s.forme === 'compose') {
        /* Un cône posé sur un cube ou un pavé est dessiné dans LEUR perspective. */
        const vue = estRond(s.bas) ? VUE_RONDE : VUE_CAVALIERE;
        const y0 = empriseDe(s.bas).hauteur;
        const bordABord = estRond(s.bas) &&
            s.haut.forme !== 'boule' &&
            Math.abs(empriseDe(s.haut).largeur - empriseDe(s.bas).largeur) < 1e-9;
        const cache = masqueDe(s.haut, y0, vue);
        const bas = perspectiveDe(s.bas, habillage(s.bas, 'bas'), {
            ...(estRond(s.bas) ? { dessus: { entier: bordABord, cache } } : {}),
        });
        /* Sous un cône, les arêtes du dessus d'un pavé que le cône cache passent en tirets. */
        if (!estRond(s.bas) && s.haut.forme === 'cone') {
            const aretes = bas.pleins.map((l) => departager(l, cache));
            bas.pleins.splice(0, bas.pleins.length, ...aretes.flatMap((x) => x.pleins));
            bas.caches.push(...aretes.flatMap((x) => x.caches));
        }
        const haut = perspectiveDe(s.haut, habillage(s.haut, 'haut'), { vue });
        /* Une boule est dessinée autour de son centre : elle TOUCHE le dessus du bas. */
        const y = s.haut.forme === 'boule' ? y0 + s.haut.rayon : y0;
        peindre(fusion(bas, haut, { x: 0, y }), { x: 150, y: HAUT, largeur: 420, hauteur: CORPS }, traces);
    }
    else {
        const h = habillage(s);
        if (deux) {
            traces.push(ecrit(180, 22, 'Le solide', 'milieu', false, 13));
            traces.push(ecrit(540, 22, 'Son développement', 'milieu', false, 13));
            peindre(perspectiveDe(s, h), { x: 0, y: HAUT, largeur: 360, hauteur: CORPS }, traces);
            peindre(developpementDe(s, h), { x: 360, y: HAUT, largeur: 360, hauteur: CORPS }, traces);
        }
        else {
            const scene = d.vue === 'perspective' ? perspectiveDe(s, h) : developpementDe(s, h);
            peindre(scene, { x: 110, y: HAUT, largeur: 500, hauteur: CORPS }, traces);
        }
    }
    /* La légende : chaque famille, par sa lettre, sa teinte ET des mots. */
    familles.forEach((f, i) => {
        const y = HAUT + CORPS + 14 + i * 24;
        traces.push({
            type: 'rect',
            x: 28,
            y: y - 14,
            largeur: 18,
            hauteur: 18,
            rayon: 3,
            surface: f.surface,
        });
        traces.push(ecrit(37, y, f.etiquette, 'milieu', true, 13));
        traces.push(ecrit(56, y, f.quoi, 'debut', false, 14));
    });
    return {
        largeur: L,
        hauteur: HAUT + CORPS + (familles.length > 0 ? 10 + familles.length * 24 : 6),
        decrit: d.decrit,
        traces,
        griefs,
    };
}
//# sourceMappingURL=solide.js.map